import React, { Suspense, lazy, useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";
import MathText from "./MathText";
import MathEditor from "./MathEditor";
import DrawingCanvas from "./DrawingCanvas";
import MathKeyboard from "./MathKeyboard";
import VideoCallModal from "./VideoCallModal";
import { qlAlert, qlConfirm } from "../utils/dialogs";
import DocumentViewer from "./DocumentViewer";
import DownloadLink from "./DownloadLink";
import { rafraichirCompteurs } from "../services/compteurs";
import { TON_TICKET, heure, resumeTicket, texteEstimation } from "../services/assistance";
import "./assistance/Assistance.css";

const CorrectionStudio = lazy(() => import("./CorrectionStudio"));

// Énoncé et corrigé affichés dans la messagerie ; le corrigé n'est envoyé par l'API qu'aux enseignants.
function SujetEtCorrige({ enonce, pdfUrl, imageUrl, questions = [], corrige, bareme }) {
  const [ouvert, setOuvert] = useState(false);
  const [plein, setPlein] = useState(null);
  const texteEnonce = [enonce, ...questions.filter(q => q.enonce).map(q => `${q.numero}. ${q.enonce}`)].filter(Boolean).join("\n\n");
  const texteCorrige = bareme ? `${corrige}\n\nBarème\n${bareme}` : corrige;
  if (!texteEnonce && !pdfUrl && !corrige) return null;
  return (
    <div className="ql-devoir-msg-content">
      <div className="ql-doc-buttons">
        {texteEnonce && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setOuvert(v => !v)}>
            <i className={`bi bi-chevron-${ouvert ? "up" : "down"} me-1`}></i>{ouvert ? "Masquer l'énoncé" : "Voir l'énoncé"}
          </button>
        )}
        {texteEnonce && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPlein({ titre: "Énoncé", texte: texteEnonce })}>
            <i className="bi bi-arrows-fullscreen me-1"></i>Plein écran
          </button>
        )}
        {pdfUrl && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setPlein({ titre: "Sujet", url: pdfUrl })}>
            <i className="bi bi-file-earmark-pdf me-1"></i>Voir le sujet
          </button>
        )}
        {corrige && (
          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setPlein({ titre: "Corrigé (enseignants)", texte: texteCorrige })}>
            <i className="bi bi-eye me-1"></i>Voir le corrigé
          </button>
        )}
      </div>
      {ouvert && (
        <div className="ql-msg-enonce" style={{ whiteSpace: "pre-wrap" }}>
          {imageUrl && <img src={imageUrl} alt="" style={{ maxWidth: "100%", borderRadius: 6, marginBottom: 8 }} />}
          <MathText text={texteEnonce} />
        </div>
      )}
      {plein && <DocumentViewer titre={plein.titre} url={plein.url} texte={plein.texte} onClose={() => setPlein(null)} />}
    </div>
  );
}

function FichierEleve({ url, libelle }) {
  const [plein, setPlein] = useState(false);
  if (!url) return null;
  return (
    <div className="ql-doc-buttons">
      <button type="button" className="btn btn-sm btn-primary" onClick={() => setPlein(true)}>
        <i className="bi bi-eye me-1"></i>Voir {libelle}
      </button>
      <DownloadLink url={url} filename={url.split("?")[0].split("/").pop()} className="btn btn-sm btn-outline-primary">
        <i className="bi bi-download me-1"></i>Télécharger
      </DownloadLink>
      {plein && <DocumentViewer titre={libelle} url={url} onClose={() => setPlein(false)} />}
    </div>
  );
}

function EnvoyerCorrigeBouton({ endpoint, dejaEnvoye, libelle, onSent }) {
  const [envoi, setEnvoi] = useState(false);
  const envoyer = () => {
    qlConfirm(`${dejaEnvoye ? "Le corrigé a déjà été envoyé. L'envoyer à nouveau" : "Envoyer le corrigé complet à cet élève"} ? À utiliser en dernier recours, après tes explications.`, async () => {
      setEnvoi(true);
      try {
        const { data } = await api.post(endpoint);
        onSent?.(data);
        qlAlert("Corrigé envoyé à l'élève.", "success");
      } catch (e) {
        qlAlert(e?.response?.data?.detail || "Envoi impossible.", "error");
      } finally {
        setEnvoi(false);
      }
    }, { okLabel: "Envoyer" });
  };
  return (
    <button type="button" className="btn btn-sm btn-outline-success mt-2" disabled={envoi} onClick={envoyer}>
      {envoi ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-send-check me-1"></i>}
      {dejaEnvoye ? `${libelle} (déjà envoyé)` : libelle}
    </button>
  );
}

function AssistanceCard({ data, user, onSent }) {
  const isTeacher = user?.is_staff || user?.is_superuser;
  if (!data) return null;
  return (
    <div className="ql-devoir-msg-card">
      <div className="ql-devoir-msg-header">
        <i className="bi bi-life-preserver me-2"></i>
        <span className="ql-devoir-msg-title">{data.exercice_title}</span>
        <span className="ql-devoir-msg-badge">Demande d'aide</span>
      </div>
      <div className="ql-devoir-msg-content">
        <div className="ql-devoir-msg-label">Réponse de l'élève :</div>
        <div className="ql-devoir-msg-body" style={{ whiteSpace: "pre-wrap" }}><MathText text={data.student_answer || "(aucune réponse rédigée)"} /></div>
      </div>
      <SujetEtCorrige
        enonce={data.exercice_description}
        imageUrl={data.exercice_image_url}
        questions={data.questions}
        corrige={(data.corrections || []).join("\n\n")}
      />
      {isTeacher && (data.corrections || []).length > 0 && (
        <EnvoyerCorrigeBouton
          endpoint={`assistances/${data.id}/envoyer-correction/`}
          dejaEnvoye={!!data.correction_envoyee_at}
          libelle="Envoyer la correction à l'élève"
          onSent={onSent}
        />
      )}
    </div>
  );
}

const fcfa = (n) => `${new Intl.NumberFormat("fr-FR").format(n || 0)} FCFA`;
const COULEUR_STATUT_DEPENSE = { brouillon: "#64748b", a_valider: "#7056c7", approuvee: "#d68600", payee: "#15a652" };

// Demande d'approbation d'une dépense, adressée aux super admins.
function DepenseValidationCard({ data, onChange }) {
  const [motif, setMotif] = useState("");
  const [refus, setRefus] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [piece, setPiece] = useState(false);
  if (!data) return null;
  const decider = async (decision) => {
    setEnvoi(true);
    try {
      const r = await api.post(`compta/depenses/${data.id}/decision/`, { decision, motif });
      onChange(r.data);
      qlAlert(decision === "approuver" ? "Dépense approuvée." : "Dépense refusée.", "success");
    } catch (e) {
      qlAlert(e?.response?.data?.detail || "Décision impossible.", "error");
    }
    setEnvoi(false);
  };
  const enAttente = data.statut === "a_valider";
  return (
    <div className="ql-devoir-msg-card" style={{ borderColor: COULEUR_STATUT_DEPENSE[data.statut] || "#7056c7" }}>
      <div className="ql-devoir-msg-header">
        <i className="bi bi-receipt me-2"></i>
        <span className="ql-devoir-msg-title">{data.titre}</span>
        <span className="ql-devoir-msg-badge" style={{ background: COULEUR_STATUT_DEPENSE[data.statut] }}>{data.statut_label}</span>
      </div>
      <div className="ql-devoir-msg-content">
        <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#e64049" }}>{fcfa(data.montant)}</div>
        <div className="ql-devoir-msg-label">{data.categorie_label}{data.fournisseur ? ` · ${data.fournisseur}` : ""} · demandée par {data.demandeur || "la comptabilité"}</div>
        {data.description && <div className="ql-devoir-msg-body" style={{ whiteSpace: "pre-wrap" }}>{data.description}</div>}
        {data.justificatif_url && (
          <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setPiece(true)}>
            <i className="bi bi-paperclip me-1"></i>Voir le justificatif
          </button>
        )}
        {piece && <DocumentViewer titre={`Justificatif — ${data.titre}`} url={data.justificatif_url} onClose={() => setPiece(false)} />}
        <div className="ql-devoir-msg-label mt-2">
          {data.reservee_principal ? "≥ 20 000 FCFA : approbation réservée au CEO." : "< 20 000 FCFA : un super admin peut l'approuver."}
        </div>
      </div>
      {enAttente && data.peut_decider && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 14px 14px" }}>
          {refus && (
            <input className="form-control form-control-sm" placeholder="Motif du refus (facultatif)" value={motif} onChange={(e) => setMotif(e.target.value)} />
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!refus && (
              <button type="button" className="btn btn-success btn-sm" disabled={envoi} onClick={() => decider("approuver")}>
                <i className="bi bi-check2-circle me-1"></i>Approuver
              </button>
            )}
            <button type="button" className="btn btn-outline-danger btn-sm" disabled={envoi}
              onClick={() => (refus ? decider("refuser") : setRefus(true))}>
              <i className="bi bi-x-circle me-1"></i>{refus ? "Confirmer le refus" : "Refuser"}
            </button>
            {refus && <button type="button" className="btn btn-light btn-sm" onClick={() => { setRefus(false); setMotif(""); }}>Annuler</button>}
          </div>
        </div>
      )}
      {enAttente && !data.peut_decider && (
        <div className="ql-devoir-msg-label" style={{ padding: "0 14px 14px" }}>
          {data.reservee_principal ? "Pour information : le CEO doit approuver cette dépense." : "En attente d'approbation par un super admin."}
        </div>
      )}
      {!enAttente && data.valide_par && (
        <div className="ql-devoir-msg-label" style={{ padding: "0 14px 14px" }}>Décision : {data.statut_label}, par {data.valide_par}.</div>
      )}
    </div>
  );
}

const COULEUR_STATUT_CONTENU = { brouillon: "#697b90", a_valider: "#d68600", valide: "#15a652", publie: "#126ef4" };

// Publication préparée par la Community Manager : le premier super admin qui
// valide ou demande une correction décide ; la carte se met à jour pour tous.
function ContenuValidationCard({ data, onChange }) {
  const [commentaire, setCommentaire] = useState("");
  const [correction, setCorrection] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [document, setDocument] = useState(false);
  if (!data) return null;
  const decider = async (decision) => {
    if (decision === "corriger" && !commentaire.trim()) {
      qlAlert("Précisez la correction attendue.", "error");
      return;
    }
    setEnvoi(true);
    try {
      const r = await api.post(`cm/contenus/${data.id}/decision/`, { decision, commentaire });
      onChange(r.data);
      qlAlert(decision === "valider" ? "Contenu validé." : "Correction demandée.", "success");
    } catch (e) {
      qlAlert(e?.response?.data?.detail || "Décision impossible.", "error");
    }
    setEnvoi(false);
  };
  const enAttente = data.statut === "a_valider";
  const date = data.date_souhaitee
    ? new Date(data.date_souhaitee).toLocaleString("fr-FR", { timeZone: "Africa/Douala", dateStyle: "medium", timeStyle: "short" })
    : null;
  return (
    <div className="ql-devoir-msg-card" style={{ borderColor: COULEUR_STATUT_CONTENU[data.statut] || "#7056c7" }}>
      <div className="ql-devoir-msg-header">
        <i className="bi bi-megaphone me-2"></i>
        <span className="ql-devoir-msg-title">{data.titre}</span>
        <span className="ql-devoir-msg-badge" style={{ background: COULEUR_STATUT_CONTENU[data.statut] }}>{data.statut_label}</span>
      </div>
      <div className="ql-devoir-msg-content">
        <div className="ql-devoir-msg-label">
          {data.canal_label} · {data.format_label}{date ? ` · prévu le ${date}` : ""} · préparé par {data.auteur || "la Community Manager"}
        </div>
        {data.campagne && <div className="ql-devoir-msg-label">Campagne : {data.campagne.titre}</div>}
        {data.texte && <div className="ql-devoir-msg-body" style={{ whiteSpace: "pre-wrap" }}>{data.texte}</div>}
        {data.media_type === "image" && data.media_url && (
          <img src={data.media_url} alt={data.texte_alternatif || data.titre}
            style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 8, marginTop: 8, objectFit: "contain" }} />
        )}
        {data.media_type === "video" && data.media_url && (
          <video src={data.media_url} controls style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 8, marginTop: 8 }} />
        )}
        {data.media_type === "pdf" && data.media_url && (
          <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setDocument(true)}>
            <i className="bi bi-file-earmark-pdf me-1"></i>Voir le document
          </button>
        )}
        {document && <DocumentViewer titre={data.titre} url={data.media_url} onClose={() => setDocument(false)} />}
        {data.lien_cible && <div className="ql-devoir-msg-label mt-2">Lien : {data.lien_cible}</div>}
      </div>
      {enAttente && data.peut_decider && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 14px 14px" }}>
          {correction && (
            <textarea className="form-control form-control-sm" rows={2} placeholder="Correction attendue"
              value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!correction && (
              <button type="button" className="btn btn-success btn-sm" disabled={envoi} onClick={() => decider("valider")}>
                <i className="bi bi-check2-circle me-1"></i>Valider
              </button>
            )}
            <button type="button" className="btn btn-outline-warning btn-sm" disabled={envoi}
              onClick={() => (correction ? decider("corriger") : setCorrection(true))}>
              <i className="bi bi-pencil me-1"></i>{correction ? "Envoyer la demande de correction" : "Demander une correction"}
            </button>
            {correction && <button type="button" className="btn btn-light btn-sm" onClick={() => { setCorrection(false); setCommentaire(""); }}>Annuler</button>}
          </div>
        </div>
      )}
      {enAttente && !data.peut_decider && (
        <div className="ql-devoir-msg-label" style={{ padding: "0 14px 14px" }}>En attente de validation par un super admin.</div>
      )}
      {!enAttente && (
        <div className="ql-devoir-msg-label" style={{ padding: "0 14px 14px" }}>
          {data.statut === "brouillon" && data.commentaire_correction ? `Correction demandée : ${data.commentaire_correction}` : null}
          {data.statut !== "brouillon" && data.valide_par ? `Validé par ${data.valide_par}.` : null}
          {data.statut === "publie" && data.url_publication ? ` Publié : ${data.url_publication}` : null}
        </div>
      )}
    </div>
  );
}

function CorrigeCard({ content, fileUrl }) {
  return (
    <div className="ql-devoir-msg-card" style={{ borderColor: "#16a34a" }}>
      <div className="ql-devoir-msg-header">
        <i className="bi bi-patch-check-fill me-2" style={{ color: "#16a34a" }}></i>
        <span className="ql-devoir-msg-title">Corrigé envoyé par l'enseignant</span>
      </div>
      <div className="ql-devoir-msg-content">
        {fileUrl
          ? <><div className="ql-devoir-msg-body">{content}</div><FichierEleve url={fileUrl} libelle="la correction" /></>
          : <div className="ql-devoir-msg-body" style={{ whiteSpace: "pre-wrap" }}><MathText text={content} /></div>}
      </div>
    </div>
  );
}

// Carte générique pour afficher un rendu (devoir ou évaluation)
// correctEndpoint : ex. "devoirs/rendus/ID/correct/" ou "evaluations/rendus/ID/correct/"
function StudioCorrection({ kind, renduId, onClose, onSent }) {
  return (
    <Suspense fallback={<div className="cs-chargement-global"><div className="spinner-border text-primary"></div></div>}>
      <CorrectionStudio kind={kind} renduId={renduId} onClose={onClose} onSent={onSent} />
    </Suspense>
  );
}

function RenduCard({ dr, user, onCorrect, correctEndpoint, label = "devoir", onCorrigeSent, onStudioSent }) {
  const isTeacher     = user?.is_staff || user?.is_superuser;
  const alreadyGraded = !!dr?.graded_by_name;
  const isMyCorrection = alreadyGraded && dr?.graded_by_id === user?.id;
  const canCorrect     = isTeacher && (!alreadyGraded || isMyCorrection);

  const [grade,      setGrade]      = useState(dr?.grade           || "");
  const [comment,    setComment]    = useState(dr?.teacher_feedback || "");
  const [corrFile,   setCorrFile]   = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err,        setErr]        = useState("");
  const [success,    setSuccess]    = useState(false);
  const [studio,     setStudio]     = useState(false);

  const handleSubmit = async () => {
    setErr(""); setSuccess(false); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("grade", grade);
      fd.append("teacher_feedback", comment);
      if (corrFile) fd.append("correction_file", corrFile);
      const res = await api.post(correctEndpoint, fd, {
        transformRequest: [(data, headers) => { delete headers["Content-Type"]; return data; }],
      });
      setSuccess(true);
      if (onCorrect) onCorrect(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur lors de la correction.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ql-devoir-msg-card">
      <div className="ql-devoir-msg-header">
        <i className="bi bi-file-earmark-text-fill me-2"></i>
        <span className="ql-devoir-msg-title">{dr?.devoir_title || label}</span>
        <span className="ql-devoir-msg-badge">Rendu élève</span>
      </div>

      <SujetEtCorrige
        enonce={dr?.devoir_description}
        pdfUrl={dr?.devoir_pdf_url}
        corrige={dr?.devoir_correction}
        bareme={dr?.devoir_bareme}
      />
      {isTeacher && label === "devoir" && dr?.devoir_correction && (
        <EnvoyerCorrigeBouton
          endpoint={`devoirs/rendus/${dr.id}/envoyer-corrige/`}
          dejaEnvoye={!!dr.corrige_envoye_at}
          libelle="Envoyer le corrigé à l'élève"
          onSent={onCorrigeSent}
        />
      )}

      {dr?.student_content && (
        <div className="ql-devoir-msg-content">
          <div className="ql-devoir-msg-label">Réponse :</div>
          <div className="ql-devoir-msg-body">{dr.student_content}</div>
        </div>
      )}

      <FichierEleve url={dr?.student_file_url} libelle="la copie de l'élève" />
      {canCorrect && dr?.student_file_url && (
        <button type="button" className="ql-studio-btn" onClick={() => setStudio(true)}>
          <i className="bi bi-pen me-2"></i>{alreadyGraded ? "Recorriger la copie (studio)" : "Corriger la copie"}
        </button>
      )}
      {studio && (
        <StudioCorrection kind={label === "devoir" ? "devoir" : "evaluation"} renduId={dr.id}
          onClose={() => setStudio(false)} onSent={(r) => onStudioSent?.(r)} />
      )}
      {isTeacher && label !== "devoir" && dr?.corrige_url && <FichierEleve url={dr.corrige_url} libelle="le corrigé (enseignants)" />}
      {label !== "devoir" && dr?.evaluation_pdf_url && <SujetEtCorrige pdfUrl={dr.evaluation_pdf_url} />}
      {isTeacher && label !== "devoir" && dr?.has_corrige_pdf && (
        dr?.grade
          ? <EnvoyerCorrigeBouton
              endpoint={`evaluations/rendus/${dr.id}/envoyer-corrige/`}
              dejaEnvoye={!!dr.corrige_envoye_at}
              libelle="Envoyer le corrigé à l'élève"
              onSent={onCorrigeSent}
            />
          : <div className="small text-muted mt-2"><i className="bi bi-info-circle me-1"></i>Mettez une note pour pouvoir envoyer le corrigé.</div>
      )}
      {isTeacher && label !== "devoir" && dr?.has_corrige_pdf === false && (
        <div className="small text-muted mt-2">
          <i className="bi bi-info-circle me-1"></i>Aucun corrigé PDF n'est encore disponible pour cette évaluation : le bouton d'envoi apparaîtra dès qu'il sera ajouté.
        </div>
      )}

      {/* Corrigé par un autre prof — lecture seule */}
      {isTeacher && alreadyGraded && !isMyCorrection && (
        <div className="ql-devoir-msg-already-graded">
          <i className="bi bi-check-circle-fill me-2 text-success"></i>
          Corrigé par <strong>{dr.graded_by_name}</strong>
          {dr.grade && <span className="ms-2">— Note : <strong>{dr.grade}</strong></span>}
        </div>
      )}

      {/* Formulaire : première correction ou mise à jour par le correcteur original */}
      {canCorrect && (
        <div className="ql-devoir-msg-correct-form">
          {isMyCorrection && (
            <div className="ql-devoir-msg-already-graded" style={{ marginBottom: 8 }}>
              <i className="bi bi-pencil-fill me-2 text-primary"></i>
              Vous avez corrigé ce {label}
              {dr.grade && <span className="ms-2">— Note actuelle : <strong>{dr.grade}</strong></span>}
            </div>
          )}
          <div className="ql-devoir-msg-label">
            {isMyCorrection ? `Modifier la correction :` : `Corriger ce ${label} :`}
          </div>
          <input
            type="text"
            className="ql-devoir-msg-grade-input"
            placeholder="Note (ex: 15/20)"
            value={grade}
            onChange={e => setGrade(e.target.value)}
          />
          <textarea
            className="ql-devoir-msg-comment-input"
            placeholder="Commentaire pour l'élève…"
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
          <label className="ql-devoir-msg-file-label">
            <i className="bi bi-paperclip me-1"></i>
            {corrFile ? corrFile.name : "Joindre un fichier de correction (optionnel)"}
            <input type="file" style={{ display: "none" }} onChange={e => setCorrFile(e.target.files[0])} />
          </label>
          {err     && <div className="ql-devoir-msg-err">{err}</div>}
          {success && <div style={{ color: "#198754", fontSize: "0.82rem", marginTop: 4 }}><i className="bi bi-check2-circle me-1"></i>Correction enregistrée.</div>}
          <button
            className="ql-devoir-msg-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || (!grade && !comment)}
          >
            {submitting ? "Envoi…" : isMyCorrection ? "Mettre à jour la correction" : "Envoyer la correction"}
          </button>
        </div>
      )}
    </div>
  );
}

function ContesterNote({ kind, dr, onAjout }) {
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [enCours, setEnCours] = useState(dr?.contestation_en_cours || null);
  if (enCours) {
    return <div className="ql-contest-etat"><i className="bi bi-hourglass-split me-1"></i>Contestation : {enCours}</div>;
  }
  const envoyer = async () => {
    setEnvoi(true);
    try {
      const r = await api.post(`correction/${kind}/${dr.id}/contester/`, { motif });
      setEnCours(r.data.contestation.statut_label);
      onAjout?.([r.data.message]);
      qlAlert("Ta contestation a été envoyée à ton enseignant.", "success");
    } catch (e) {
      qlAlert(e.response?.data?.detail || "Envoi impossible.", "error");
    } finally { setEnvoi(false); }
  };
  if (!ouvert) {
    return (
      <button type="button" className="ql-contest-btn" onClick={() => setOuvert(true)}>
        <i className="bi bi-flag me-1"></i>Contester la note
      </button>
    );
  }
  return (
    <div className="ql-contest-form">
      <textarea rows={3} maxLength={3000} placeholder="Explique précisément ce qui, selon toi, mérite d'être revu (question, page…)"
        value={motif} onChange={e => setMotif(e.target.value)} />
      <div className="ql-contest-actions">
        <button type="button" className="ql-devoir-msg-dl-btn" onClick={() => setOuvert(false)}>Annuler</button>
        <button type="button" className="ql-devoir-msg-submit-btn" disabled={envoi || motif.trim().length < 10} onClick={envoyer}>
          {envoi ? "Envoi…" : "Envoyer la contestation"}
        </button>
      </div>
    </div>
  );
}

const STATUT_CONTEST = { en_attente: "#f59e0b", acceptee: "#1767e8", refusee: "#6b7280", recorrigee: "#16a34a" };

function ContestationCard({ data, user, onAjout, onChange }) {
  const [reponse, setReponse] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [studio, setStudio] = useState(false);
  if (!data) return null;
  const isTeacher = user?.is_staff || user?.is_superuser;
  const decider = async (decision) => {
    if (decision === "refuser" && !reponse.trim()) { qlAlert("Expliquez à l'élève pourquoi la note est maintenue.", "info"); return; }
    setEnvoi(true);
    try {
      const r = await api.post(`contestations/${data.id}/decision/`, { decision, reponse });
      onChange?.(r.data.contestation);
      onAjout?.([r.data.message]);
      if (decision === "accepter") setStudio(true);
    } catch (e) {
      qlAlert(e.response?.data?.detail || "Décision impossible.", "error");
    } finally { setEnvoi(false); }
  };
  return (
    <div className="ql-devoir-msg-card ql-contest-card">
      <div className="ql-devoir-msg-header">
        <i className="bi bi-flag-fill me-2" style={{ color: "#bd2331" }}></i>
        <span className="ql-devoir-msg-title">Contestation de note</span>
        <span className="ql-devoir-msg-badge" style={{ background: STATUT_CONTEST[data.statut] || "#6b7280", color: "#fff" }}>{data.statut_label}</span>
      </div>
      <div className="ql-devoir-msg-content">
        <div className="ql-devoir-msg-label">Note contestée : <strong>{data.note_contestee || "—"}</strong>
          {data.nouvelle_note && <> · nouvelle note : <strong>{data.nouvelle_note}</strong></>}</div>
        <div className="ql-devoir-msg-body" style={{ whiteSpace: "pre-wrap" }}>{data.motif}</div>
        {data.reponse && <div className="ql-devoir-msg-body mt-2"><strong>Réponse :</strong> {data.reponse}</div>}
      </div>
      {isTeacher && data.statut === "en_attente" && (
        <div className="ql-devoir-msg-correct-form">
          <textarea className="ql-devoir-msg-comment-input" rows={2} placeholder="Réponse à l'élève (obligatoire en cas de refus)"
            value={reponse} onChange={e => setReponse(e.target.value)} />
          <div className="ql-contest-actions">
            <button type="button" className="ql-devoir-msg-dl-btn" disabled={envoi} onClick={() => decider("refuser")}>Refuser (note maintenue)</button>
            <button type="button" className="ql-devoir-msg-submit-btn" disabled={envoi} onClick={() => decider("accepter")}>Accepter et recorriger</button>
          </div>
        </div>
      )}
      {isTeacher && data.statut === "acceptee" && (
        <button type="button" className="ql-studio-btn" onClick={() => setStudio(true)}><i className="bi bi-pen me-2"></i>Recorriger la copie</button>
      )}
      {studio && (
        <StudioCorrection kind={data.kind} renduId={data.rendu_id} onClose={() => setStudio(false)}
          onSent={(r) => { onAjout?.(r.messages); onChange?.({ ...data, statut: "recorrigee", statut_label: "Recorrigée", nouvelle_note: r.note }); }} />
      )}
    </div>
  );
}

function CorrectionCard({ dr, label = "Devoir", fileUrl, user, kind, onAjout }) {
  const [showImg, setShowImg] = useState(false);
  const url = fileUrl || dr?.correction_file_url;
  const isStudent = user && !(user.is_staff || user.is_superuser);
  const ext = url ? url.split("?")[0].split(".").pop().toLowerCase() : "";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
  const isPdf   = ext === "pdf";

  return (
    <div className="ql-devoir-msg-card ql-devoir-corr-card">
      <div className="ql-devoir-msg-header">
        <i className="bi bi-patch-check-fill me-2 text-success"></i>
        <span className="ql-devoir-msg-title">{dr?.devoir_title || label}</span>
        <span className="ql-devoir-msg-badge ql-badge-corr">Correction</span>
      </div>
      {dr?.grade && (
        <div className="ql-devoir-corr-grade">
          Note : <strong>{dr.grade}</strong>
        </div>
      )}
      {dr?.teacher_feedback && (
        <div className="ql-devoir-msg-content">
          <div className="ql-devoir-msg-label">Commentaire du professeur :</div>
          <div className="ql-devoir-msg-body">{dr.teacher_feedback}</div>
        </div>
      )}
      {isStudent && dr?.grade && kind && <ContesterNote kind={kind} dr={dr} onAjout={onAjout} />}
      {url && (
        <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {isImage && (
              <button
                className="ql-devoir-msg-dl-btn"
                style={{ border: "none", cursor: "pointer" }}
                onClick={() => setShowImg(v => !v)}
              >
                <i className={`bi bi-${showImg ? "chevron-up" : "eye"} me-1`}></i>
                {showImg ? "Masquer" : "Voir l'image"}
              </button>
            )}
            {isPdf && (
              <DownloadLink url={url} filename="correction.pdf" mimeType="application/pdf" downloadOnWeb={false} titre="Correction" className="ql-devoir-msg-dl-btn">
                <i className="bi bi-eye me-1"></i>Ouvrir le PDF
              </DownloadLink>
            )}
            <DownloadLink url={url} filename={url.split("?")[0].split("/").pop()} className="ql-devoir-msg-dl-btn">
              <i className="bi bi-download me-1"></i>Télécharger
            </DownloadLink>
          </div>
          {isImage && showImg && (
            <img
              src={url}
              alt="Correction"
              style={{ maxWidth: "100%", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,.15)", marginTop: 4 }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Avatar({ src, name, size = "md" }) {
  const letters = (name || "?")[0].toUpperCase();
  const sz = { xs: 28, sm: 36, md: 44, lg: 56 }[size] || 44;
  if (src) return <img src={src} alt="" style={{ width: sz, height: sz, borderRadius: "50%", objectFit: "cover" }} />;
  return (
    <div style={{
      width: sz, height: sz, borderRadius: "50%", background: "#dbeafe",
      color: "#1d4ed8", display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: sz * 0.4,
    }}>{letters}</div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays < 7) return ["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"][d.getDay()];
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

function fileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function lastMsgPreview(msg) {
  if (!msg) return "";
  if (msg.deleted)              return "Message supprimé";
  if (msg.msg_type === "pdf")               return "📎 Document PDF";
  if (msg.msg_type === "audio")             return "🎤 Message vocal";
  if (msg.msg_type === "video")             return "🎥 Vidéo";
  if (msg.msg_type === "image")             return "🖼 Image";
  if (msg.msg_type === "devoir_rendu")      return `📝 Rendu : ${msg.devoir_rendu?.devoir_title || "Devoir"}`;
  if (msg.msg_type === "devoir_correction") return `✅ Correction : ${msg.devoir_rendu?.devoir_title || "Devoir"}`;
  if (msg.msg_type === "eval_rendu")        return `📝 Rendu : ${msg.evaluation_rendu?.devoir_title || "Évaluation"}`;
  if (msg.msg_type === "eval_correction")   return `✅ Correction : ${msg.evaluation_rendu?.devoir_title || "Évaluation"}`;
  if (msg.msg_type === "contestation")      return `⚖️ Contestation : ${(msg.evaluation_rendu || msg.devoir_rendu)?.devoir_title || "note"}`;
  if (msg.msg_type === "appel")             return `📞 ${msg.content}`;
  if (msg.msg_type === "assistance")        return `🆘 ${msg.content}`;
  if (msg.msg_type === "corrige")           return "✅ Corrigé envoyé";
  if (msg.msg_type === "depense_validation") return `🧾 ${msg.content}`;
  if (msg.msg_type === "contenu_validation") return `📣 ${msg.content}`;
  return msg.content;
}

// perimetre : « tout » (comportement d'origine), « interne » (personnel : échanges avec l'équipe,
// les parents, les générateurs) ou « assistance » (personnel : élèves, groupes, rendus, avec la
// file d'attente). Le compositeur (formules, clavier mathématique, dessin, fichiers, voix) est le même partout.
export default function MessagesView({
  user, compact = false, perimetre = "tout", titre = "Messages", libelleNouveau = "Nouveau message",
  onNouveau = null, convAOuvrir = null, onChange = null,
}) {
  const [conversations, setConversations]   = useState([]);
  const [activeConv,    setActiveConvState] = useState(null);
  const [messages,      setMessages]        = useState([]);
  const [msgHasContent, setMsgHasContent]   = useState(false);
  const [tab,           setTab]             = useState("inbox");
  const [contacts,      setContacts]        = useState([]);
  const [showNewConv,      setShowNewConv]      = useState(false);
  const [sending,          setSending]         = useState(false);
  const [sendError,        setSendError]       = useState("");
  const [showGroupMembers,   setShowGroupMembers]   = useState(false);
  const [memberAction,       setMemberAction]       = useState(null);
  const [groupesDisponibles, setGroupesDisponibles] = useState([]);
  const [joiningGroupeId,    setJoiningGroupeId]    = useState(null);
  const [replyTo,            setReplyTo]            = useState(null);
  const [hoveredMsgId,       setHoveredMsgId]       = useState(null);
  const [editingMsg,         setEditingMsg]         = useState(null);  // { id, content }
  const [editContent,        setEditContent]        = useState("");
  const [pendingFile,        setPendingFile]        = useState(null);  // { file, previewUrl, type }
  const [recording,     setRecording]       = useState(false);
  const [searchQ,       setSearchQ]         = useState("");
  const [loadingMsgs,   setLoadingMsgs]     = useState(false);
  const [showDrawing,   setShowDrawing]     = useState(false);
  const [lightboxSrc,   setLightboxSrc]     = useState(null);
  const [showMathKb,    setShowMathKb]      = useState(false);
  const [callModal,     setCallModal]       = useState(null); // { targetUserId, targetName } | null
  const [showReassign,  setShowReassign]    = useState(false);
  const [reassignTeachers, setReassignTeachers] = useState([]);
  const [reassignLoading, setReassignLoading] = useState(false);

  const messagesEndRef   = useRef(null);
  const messagesThreadRef = useRef(null);
  const isAtBottomRef    = useRef(true);
  const fileInputRef     = useRef(null);
  const _taRef        = useRef(null); // textarea DOM ref
  const mathEditorRef = useRef({     // API compatible avec MathEditor
    getValue: () => _taRef.current?.value || "",
    setValue: (v) => { if (_taRef.current) { _taRef.current.value = v || ""; _taRef.current.style.height = "auto"; } },
    focus:    () => _taRef.current?.focus(),
  });
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const pollRef          = useRef(null);
  const activeConvRef    = useRef(null);
  // Studio de correction / contestations : on recharge la conversation ouverte (les
  // nouveaux messages peuvent appartenir à la conversation privée élève–enseignant).
  const ajouterMessages = () => {
    const conv = activeConvRef.current;
    if (conv) api.get(`messages/${conv.id}/`).then(r => setMessages(r.data.messages)).catch(() => {});
  };
  const nbMessagesRef    = useRef(0);

  // ── Clavier mobile (visualViewport) ────
  // Android : la zone visible rétrécit, le panneau prend sa hauteur.
  // iPhone : Safari et l'application font défiler la page vers le haut au lieu de la rétrécir,
  // ce qui laissait un grand vide sous la conversation. Tant que le clavier est ouvert, le
  // panneau est donc fixé exactement sur la zone visible (position et hauteur).
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent || "") || document.documentElement.classList.contains("ql-ios");
    let hauteurMax = vv.height;
    // Les styles mobiles imposent la position du panneau avec !important : on fait de même.
    const PROPRIETES = ["position", "top", "left", "right", "bottom", "z-index", "height", "transform"];
    const reinitialiser = (panneau) => {
      PROPRIETES.forEach((k) => panneau.style.removeProperty(k));
      document.body.classList.remove("ql-clavier-ouvert");
    };
    const onResize = () => {
      const chatPanel = document.querySelector(".ql-msg-chat-panel");
      if (!chatPanel) return;
      hauteurMax = Math.max(hauteurMax, vv.height, ios ? 0 : window.innerHeight);
      if (!ios) {
        chatPanel.style.height = vv.height + "px";
        return;
      }
      const clavierOuvert = hauteurMax - vv.height > 120 && window.innerWidth <= 900;
      if (!clavierOuvert) { reinitialiser(chatPanel); return; }
      const valeurs = { position: "fixed", top: `${vv.offsetTop}px`, left: "0", right: "0", bottom: "auto",
        "z-index": "1500", height: `${vv.height}px`, transform: "none" };
      Object.entries(valeurs).forEach(([k, v]) => chatPanel.style.setProperty(k, v, "important"));
      document.body.classList.add("ql-clavier-ouvert");
      messagesEndRef.current?.scrollIntoView({ block: "end" });
    };
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
      const chatPanel = document.querySelector(".ql-msg-chat-panel");
      if (chatPanel) reinitialiser(chatPanel);
    };
  }, []);

  // ── Brouillon par conversation ──────────────────────
  const insertAtCursor = (text) => {
    const ta = _taRef.current;
    if (!ta) return;
    const s = ta.selectionStart ?? ta.value.length;
    const e = ta.selectionEnd   ?? ta.value.length;
    const newVal = ta.value.slice(0, s) + text + ta.value.slice(e);
    ta.value = newVal;
    ta.selectionStart = ta.selectionEnd = s + text.length;
    setMsgHasContent(!!newVal.trim());
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
    ta.focus();
  };

  const draftKey   = (id) => `ql_msg_draft_${id}`;
  const saveDraft  = (id, v) => v?.trim()
    ? localStorage.setItem(draftKey(id), v)
    : localStorage.removeItem(draftKey(id));
  const loadDraft  = (id) => localStorage.getItem(draftKey(id)) || "";
  const clearDraft = (id) => localStorage.removeItem(draftKey(id));

  // ── Fetch conversations list ─────────────────────────
  const fetchConversations = useCallback(() => {
    api.get("messages/").then(r => setConversations(r.data)).catch(() => {});
  }, []);

  const isStudent = user && !user.is_staff && !user.is_superuser;
  // Conversation « élève » : groupe de classe ou échange privé avec un élève.
  const convEleve = (c) => c.is_groupe || !!c.other_participant?.est_eleve;
  const dansPerimetre = (c) => (perimetre === "interne" ? !convEleve(c) : perimetre === "assistance" ? convEleve(c) : true);
  const contactsVisibles = contacts.filter((c) => (
    perimetre === "interne" ? !c.est_eleve : perimetre === "assistance" ? c.est_eleve : true));
  const modeFile = perimetre === "assistance" && !isStudent;

  const fetchGroupesDisponibles = useCallback(() => {
    if (!isStudent) return;
    api.get("groupes/disponibles/").then(r => setGroupesDisponibles(r.data)).catch(() => {});
  }, [isStudent]);

  useEffect(() => {
    fetchConversations();
    api.get("messages/contacts/").then(r => setContacts(r.data)).catch(() => {});
    fetchGroupesDisponibles();
  }, [fetchConversations, fetchGroupesDisponibles]);

  const closeConv = useCallback(() => {
    setActiveConvState(null);
    activeConvRef.current = null;
    clearInterval(pollRef.current);
    setMessages([]);
  }, []);

  // (insertAtCursor no longer needed — MathLive handles its own input)

  // ── Restaurer le brouillon quand la conversation active change ────────────
  useEffect(() => {
    if (!activeConv?.id) return;
    const draft = loadDraft(activeConv.id);
    setTimeout(() => {
      if (draft) {
        mathEditorRef.current?.setValue(draft);
        setMsgHasContent(true);
      } else {
        mathEditorRef.current?.setValue("");
        setMsgHasContent(false);
      }
    }, 120);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConv?.id]);

  // ── Set active conversation ─────────────────────────
  const setActiveConv = useCallback((conv) => {
    setActiveConvState(conv);
    activeConvRef.current = conv;
    clearInterval(pollRef.current);
    if (!conv) return;
    setLoadingMsgs(true);
    api.get(`messages/${conv.id}/`).then(r => {
      nbMessagesRef.current = r.data.messages.length;
      setMessages(r.data.messages);
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
      rafraichirCompteurs();
    }).catch(() => {}).finally(() => setLoadingMsgs(false));
    // Poll every 4s
    pollRef.current = setInterval(() => {
      const cur = activeConvRef.current;
      if (!cur) return;
      api.get(`messages/${cur.id}/`).then(r => {
        // Message reçu pendant la lecture : il est lu, la cloche et les pastilles suivent.
        if (r.data.messages.length !== nbMessagesRef.current) rafraichirCompteurs();
        nbMessagesRef.current = r.data.messages.length;
        setMessages(r.data.messages);
        setConversations(prev => prev.map(c => c.id === cur.id ? { ...c, unread_count: 0 } : c));
      }).catch(() => {});
      fetchConversations();
    }, 4000);
  }, [fetchConversations]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    if (!convAOuvrir?.id) return;
    api.get("messages/").then((r) => {
      setConversations(r.data);
      const conv = r.data.find((c) => c.id === convAOuvrir.id);
      if (conv) setActiveConv(conv);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convAOuvrir?.id, convAOuvrir?.cle]);

  // ── Assistance : prise en charge / clôture d'une demande ──
  const actionTicket = async (ticket, action) => {
    try {
      await api.post(`assistance/tickets/${ticket.id}/action/`, { action });
      fetchConversations();
      rafraichirCompteurs();
      onChange?.();
    } catch (e) {
      qlAlert(e.response?.data?.detail || "Action impossible.", "error");
      fetchConversations();
    }
  };

  // ── Suivi de position de scroll ──────────────────────
  useEffect(() => {
    const el = messagesThreadRef.current;
    if (!el) return;
    const onScroll = () => {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeConv?.id]);

  // ── Scroll initial à l'ouverture d'une conversation ──
  useEffect(() => {
    if (!activeConv?.id) return;
    isAtBottomRef.current = true;
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    }, 60);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConv?.id]);

  // ── Auto-scroll seulement si l'utilisateur est en bas ─
  useEffect(() => {
    if (!messages.length) return;
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // ── Send message ─────────────────────────────────────
  const sendMessage = async (file = null, fileType = null) => {
    if (!activeConvRef.current) return;
    const effectiveFile = file || pendingFile?.file || null;
    const effectiveType = fileType || pendingFile?.type || null;
    const content = mathEditorRef.current?.getValue()?.trim() || "";
    if (!effectiveFile && !content) return;
    setSending(true);
    setSendError("");
    const replyId = replyTo?.id ?? null;
    try {
      const fd = new FormData();
      if (effectiveFile) fd.append("file", effectiveFile);
      if (effectiveType && effectiveType !== "text") fd.append("msg_type", effectiveType);
      if (content) fd.append("content", content);
      if (replyId) fd.append("reply_to_id", replyId);
      await api.post(`messages/${activeConvRef.current.id}/`, fd, {
        transformRequest: [(data, headers) => { delete headers["Content-Type"]; return data; }],
      });
      // Clear MathEditor + brouillon
      clearDraft(activeConvRef.current.id);
      mathEditorRef.current?.setValue("");
      setMsgHasContent(false);
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setReplyTo(null);
      const r = await api.get(`messages/${activeConvRef.current.id}/`);
      setMessages(r.data.messages);
      fetchConversations();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erreur lors de l'envoi.";
      setSendError(detail);
      setTimeout(() => setSendError(""), 4000);
    }
    setSending(false);
  };

  // ── Envoi d'un dessin ───────────────────────────────
  const handleSendDrawing = useCallback((file) => {
    setShowDrawing(false);
    sendMessage(file, "image");
  }, [sendMessage]); // eslint-disable-line

  // ── Voice recording ──────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const f = new File([blob], `vocal_${Date.now()}.webm`, { type: "audio/webm" });
        sendMessage(f, "audio");
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecording(true);
    } catch { qlAlert("Microphone non disponible.", "error"); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  // ── File input ───────────────────────────────────────
  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    let type = null;
    if (f.type === "application/pdf")      type = "pdf";
    else if (f.type.startsWith("image/")) type = "image";
    else if (f.type.startsWith("video/")) type = "video";
    if (!type) { qlAlert("Fichiers acceptés : images, PDF, vidéos.", "warning"); return; }
    if (f.size > 10 * 1024 * 1024) { qlAlert("Fichier trop lourd (max 10 Mo).", "warning"); return; }
    const previewUrl = type === "image" ? URL.createObjectURL(f) : null;
    setPendingFile({ file: f, previewUrl, type });
    mathEditorRef.current?.focus();
  };

  // ── Create conversation ──────────────────────────────
  const createConversation = async (teacherId) => {
    const existing = conversations.find(c => c.other_participant?.id === teacherId);
    if (existing) { setActiveConv(existing); setShowNewConv(false); return; }
    try {
      const r = await api.post("messages/", { participant_id: teacherId });
      const conv = r.data;
      setConversations(prev => [conv, ...prev]);
      setActiveConv(conv);
      setShowNewConv(false);
    } catch {}
  };

  // ── Réassigner la conversation à un autre enseignant ─────
  const doReassign = async (teacherId) => {
    if (!activeConv) return;
    try {
      await api.post(`messages/${activeConv.id}/reassigner/`, { teacher_id: teacherId });
      setShowReassign(false);
      qlAlert("Conversation transférée avec succès.", "success");
      const r = await api.get(`messages/${activeConv.id}/`);
      setMessages(r.data.messages);
      fetchConversations();
    } catch (err) {
      qlAlert(err.response?.data?.detail || "Impossible de transférer cette conversation.", "error");
    }
  };

  // ── Rejoindre un groupe disponible ───────────────────
  const rejoindreGroupe = async (groupeId) => {
    setJoiningGroupeId(groupeId);
    try {
      await api.post(`groupes/${groupeId}/rejoindre/`);
      fetchConversations();
      fetchGroupesDisponibles();
      setTab("groupes");
    } catch (e) {
      qlAlert(e.response?.data?.detail || "Impossible de rejoindre le groupe.", "error");
    }
    setJoiningGroupeId(null);
  };

  // ── Action sur membre de groupe ──────────────────────
  const doMemberAction = async (groupeId, userId, action) => {
    try {
      await api.patch(`groupes/${groupeId}/membres/${userId}/`, { action });
      // Rafraîchir la liste ET mettre à jour activeConv avec les nouvelles données
      const r = await api.get("messages/");
      setConversations(r.data);
      const updated = r.data.find(c => c.id === activeConvRef.current?.id);
      if (updated) {
        setActiveConvState(updated);
        activeConvRef.current = updated;
      }
    } catch (e) {
      qlAlert(e.response?.data?.detail || "Erreur.", "error");
    }
    setMemberAction(null);
  };

  // ── Modifier un message envoyé ───────────────────
  const startEdit = (msg) => {
    setEditingMsg(msg);
    setEditContent(msg.content);
    setHoveredMsgId(null);
  };

  const saveEdit = async () => {
    if (!editingMsg || !editContent.trim()) return;
    try {
      const r = await api.patch(`messages/${activeConvRef.current.id}/${editingMsg.id}/edit/`, { content: editContent.trim() });
      setMessages(prev => prev.map(m => m.id === editingMsg.id ? r.data : m));
    } catch (e) {
      qlAlert(e.response?.data?.detail || "Impossible de modifier le message.", "error");
    }
    setEditingMsg(null);
    setEditContent("");
  };

  const cancelEdit = () => { setEditingMsg(null); setEditContent(""); };

  // ── Supprimer un message ─────────────────────────────
  const deleteMsg = (msg) => {
    qlConfirm("Supprimer ce message ?", async () => {
      try {
        const r = await api.delete(`messages/${activeConvRef.current.id}/${msg.id}/delete/`);
        setMessages(prev => prev.map(m => m.id === msg.id ? r.data : m));
      } catch (e) {
        qlAlert(e.response?.data?.detail || "Impossible de supprimer ce message.", "error");
      }
    }, { danger: true, okLabel: "Supprimer" });
  };

  // ── Filter conversations ─────────────────────────────
  const conversationsVisibles = conversations.filter(dansPerimetre);
  // Élève : ses groupes de classe sont dans l'onglet « Groupes », pas dans la boîte de réception.
  const groupesAPart = isStudent && !modeFile;
  const filtered = conversationsVisibles.filter(c => {
    if (tab === "groupes")  return c.is_groupe && !c.is_archived;
    if (groupesAPart && c.is_groupe && tab !== "archived") return false;
    if (tab === "unread")   return c.unread_count > 0;
    if (tab === "archived") return c.is_archived;
    if (tab === "attente")  return c.ticket?.statut === "en_attente";
    if (tab === "cours")    return c.ticket?.statut === "en_cours";
    if (tab === "terminees") return ["terminee", "reattribuee"].includes(c.ticket?.statut);
    return !c.is_archived;
  }).filter(c => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    if (c.is_groupe) return c.groupe?.name?.toLowerCase().includes(q);
    return c.other_participant?.nom_complet?.toLowerCase().includes(q);
  });

  if (modeFile) {
    // File d'attente : d'abord les demandes en attente par ordre d'arrivée, puis celles en cours.
    const poids = (c) => (c.ticket?.statut === "en_attente" ? 0 : c.ticket?.statut === "en_cours" ? 1 : 2);
    filtered.sort((a, b) => poids(a) - poids(b)
      || (poids(a) === 0 ? new Date(a.ticket.arrivee) - new Date(b.ticket.arrivee) : 0));
  }
  const unreadCount = conversationsVisibles.filter(c => c.unread_count > 0 && !(groupesAPart && c.is_groupe)).length;
  const groupesNonLus = conversationsVisibles.filter(c => c.is_groupe && !c.is_archived && c.unread_count > 0).length;
  const compteTickets = (statut) => conversationsVisibles.filter(c => c.ticket?.statut === statut).length;
  // La conversation ouverte est relue dans la liste rafraîchie (statut et estimation à jour).
  const convActive = (activeConv && conversations.find(c => c.id === activeConv.id)) || activeConv;
  const ticketActif = convActive?.ticket;

  const convOpen = !!activeConv;

  return (
    <div className={`ql-msg-wrap${convOpen ? " conv-open" : ""}${compact ? " compact" : ""}`}>

      {/* ── LEFT: conversation list ─────────────────── */}
      <div className="ql-msg-list-panel">
        <div className="ql-msg-list-top">
          {!compact && (
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0" style={{ fontSize: "1.05rem" }}>{titre}</h5>
              <button className="btn btn-primary btn-sm" onClick={() => (onNouveau ? onNouveau() : setShowNewConv(true))}>
                <i className="bi bi-pencil-square me-1"></i>{libelleNouveau}
              </button>
            </div>
          )}
          {compact && (
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-bold" style={{ fontSize: "0.9rem", color: "#64748b" }}>{modeFile ? "File d'assistance" : "Conversations"}</span>
              <button className="ql-msg-action-btn" onClick={() => (onNouveau ? onNouveau() : setShowNewConv(true))} title={libelleNouveau}>
                <i className="bi bi-pencil-square"></i>
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="ql-msg-tabs">
            <button className={`ql-msg-tab${tab === "inbox" ? " active" : ""}`} onClick={() => setTab("inbox")}>
              {modeFile ? "Toutes" : "Boîte de réception"}
            </button>
            {modeFile && <>
              <button className={`ql-msg-tab${tab === "attente" ? " active" : ""}`} onClick={() => setTab("attente")}>
                En attente {compteTickets("en_attente") > 0 && <span className="ql-msg-tab-badge">{compteTickets("en_attente")}</span>}
              </button>
              <button className={`ql-msg-tab${tab === "cours" ? " active" : ""}`} onClick={() => setTab("cours")}>
                En cours {compteTickets("en_cours") > 0 && <span className="ql-msg-tab-badge ql-msg-tab-badge--bleu">{compteTickets("en_cours")}</span>}
              </button>
              <button className={`ql-msg-tab${tab === "terminees" ? " active" : ""}`} onClick={() => setTab("terminees")}>Terminées</button>
            </>}
            <button className={`ql-msg-tab${tab === "unread" ? " active" : ""}`} onClick={() => setTab("unread")}>
              Non lus {unreadCount > 0 && <span className="ql-msg-tab-badge">{unreadCount}</span>}
            </button>
            {isStudent && (
              <button className={`ql-msg-tab${tab === "groupes" ? " active" : ""}`}
                onClick={() => { setTab("groupes"); fetchGroupesDisponibles(); }}>
                Groupes {groupesNonLus > 0 && <span className="ql-msg-tab-badge">{groupesNonLus}</span>}
              </button>
            )}
            <button className={`ql-msg-tab${tab === "archived" ? " active" : ""}`} onClick={() => setTab("archived")}>
              Archivés
            </button>
          </div>

          {/* Search */}
          <div className="ql-msg-search">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Rechercher une conversation..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
            />
          </div>
        </div>

        <div className="ql-msg-conv-list">
          {tab === "groupes" && filtered.length === 0 && groupesDisponibles.length === 0 && (
            <div className="ql-msg-empty">
              <i className="bi bi-people" style={{ fontSize: "2rem", color: "#cbd5e1" }}></i>
              <p className="mt-2" style={{ fontSize: "0.85rem", color: "#94a3b8" }}>Aucun groupe pour l'instant</p>
            </div>
          )}
          {tab === "groupes" && filtered.length > 0 && groupesDisponibles.length > 0 && (
            <div className="ql-msg-section-titre">Mes groupes</div>
          )}
          {tab !== "groupes" && filtered.length === 0 && !convOpen && (
            <div className="ql-msg-empty">Aucune conversation</div>
          )}
          {filtered.map(c => {
            const isActive = activeConv?.id === c.id;
            if (c.is_groupe) {
              const g = c.groupe || {};
              return (
                <div
                  key={c.id}
                  className={`ql-msg-conv-row ql-msg-conv-row--groupe${isActive ? " active" : ""}${c.unread_count > 0 ? " unread" : ""}`}
                  onClick={() => { setActiveConv(c); setShowGroupMembers(false); }}
                >
                  <div className="ql-msg-conv-av">
                    <div className="ql-msg-groupe-av">
                      {g.photo_url
                        ? <img src={g.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} />
                        : <i className="bi bi-people-fill"></i>}
                    </div>
                    {c.unread_count > 0 && <span className="ql-msg-mini-badge">{c.unread_count}</span>}
                  </div>
                  <div className="ql-msg-conv-body">
                    <div className="ql-msg-conv-name">
                      <i className="bi bi-people me-1" style={{ fontSize: "0.72rem", color: "#6366f1" }}></i>
                      {g.name}
                    </div>
                    <div className="ql-msg-conv-subject" translate="no" style={{ color: "#64748b" }}>{g.sujet || lastMsgPreview(c.last_message)}</div>
                  </div>
                  <div className="ql-msg-conv-meta">
                    <span className="ql-msg-conv-time">{formatTime(c.last_message?.created_at)}</span>
                    {c.unread_count > 0 && <span className="ql-msg-unread-badge">{c.unread_count}</span>}
                  </div>
                </div>
              );
            }
            const other = c.other_participant || {};
            return (
              <div
                key={c.id}
                className={`ql-msg-conv-row${isActive ? " active" : ""}${c.unread_count > 0 ? " unread" : ""}`}
                onClick={() => setActiveConv(c)}
                title={convOpen ? (other.nom_complet || other.username) : undefined}
              >
                <div className="ql-msg-conv-av">
                  <Avatar src={other.photo_url} name={other.nom_complet} size="md" />
                  {other.is_online && <span className="ql-msg-online"></span>}
                  {c.unread_count > 0 && <span className="ql-msg-mini-badge">{c.unread_count}</span>}
                </div>
                <div className="ql-msg-conv-body">
                  <div className="ql-msg-conv-name" translate="no">{other.nom_complet || other.username}</div>
                  {c.ticket && (perimetre === "assistance" || isStudent) && (
                    <div className={`ql-ticket-chip ql-ticket-chip--${TON_TICKET[c.ticket.statut]}`}>
                      {resumeTicket(c.ticket)}
                      {c.ticket.matiere && <span> · {c.ticket.matiere.name}</span>}
                    </div>
                  )}
                  {other.matieres?.length > 0
                    ? <div className="ql-msg-conv-subject">{other.matieres.map(m => m.name).join(", ")}</div>
                    : other.classe
                      ? <div className="ql-msg-conv-subject" style={{ color: "#6366f1" }}>
                          <i className="bi bi-mortarboard me-1" style={{ fontSize: "0.68rem" }}></i>{other.classe}
                        </div>
                      : <div className="ql-msg-conv-last" translate="no">{lastMsgPreview(c.last_message)}</div>
                  }
                </div>
                <div className="ql-msg-conv-meta">
                  <span className="ql-msg-conv-time">{formatTime(c.last_message?.created_at)}</span>
                  {c.unread_count > 0 && <span className="ql-msg-unread-badge">{c.unread_count}</span>}
                </div>
              </div>
            );
          })}
          {/* ── Groupes disponibles (onglet "Groupes") ── */}
          {tab === "groupes" && groupesDisponibles.length > 0 && (
            <>
              <div className="ql-msg-section-titre">Groupes à rejoindre</div>
              {groupesDisponibles.map(g => (
                <div key={g.id} className="ql-msg-groupe-dispo-card">
                  <div className="ql-msg-groupe-dispo-av">
                    {g.photo_url
                      ? <img src={g.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} />
                      : <i className="bi bi-people-fill"></i>}
                  </div>
                  <div className="ql-msg-groupe-dispo-info">
                    <div className="ql-msg-groupe-dispo-name">{g.name}</div>
                    {g.sujet && <div className="ql-msg-groupe-dispo-sujet">{g.sujet}</div>}
                    <div className="ql-msg-groupe-dispo-meta">
                      <span><i className="bi bi-people me-1"></i>{g.nb_membres}/{g.max_membres} membres</span>
                      {g.teacher_name && <span style={{ marginLeft: 8 }}><i className="bi bi-person-badge me-1"></i><span style={{ color: "#94a3b8", marginRight: 3 }}>Professeur :</span>{g.teacher_name}</span>}
                    </div>
                  </div>
                  <div className="ql-msg-groupe-dispo-action">
                    {g.exclu ? (
                      <span className="badge bg-danger" style={{ fontSize: "0.72rem" }}>Exclu</span>
                    ) : g.complet ? (
                      <span className="badge bg-secondary" style={{ fontSize: "0.72rem" }}>Complet</span>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ fontSize: "0.78rem", padding: "4px 10px" }}
                        disabled={joiningGroupeId === g.id}
                        onClick={() => rejoindreGroupe(g.id)}
                      >
                        {joiningGroupeId === g.id
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : <><i className="bi bi-person-plus-fill me-1"></i>Rejoindre</>
                        }
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── CENTER: chat thread ─────────────────────── */}
      <div className="ql-msg-chat-panel">
        {!activeConv ? (
          <div className="ql-msg-no-conv">
            <i className="bi bi-chat-dots" style={{ fontSize: "3rem", color: "#cbd5e1" }}></i>
            <p className="text-muted mt-3">Sélectionnez une conversation pour commencer</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="ql-msg-chat-header">
              <button className="ql-msg-back-btn" onClick={closeConv} title="Retour">
                <i className="bi bi-chevron-left"></i>
              </button>
              {activeConv.is_groupe ? (
                <>
                  <div className="ql-msg-groupe-av" style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0 }}>
                    {activeConv.groupe?.photo_url
                      ? <img src={activeConv.groupe.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} />
                      : <i className="bi bi-people-fill" style={{ fontSize: "1.1rem", color: "#6366f1" }}></i>}
                  </div>
                  <div className="ms-2 flex-grow-1">
                    <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                      <i className="bi bi-people me-1" style={{ color: "#6366f1", fontSize: "0.8rem" }}></i>
                      {activeConv.groupe?.name}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
                      {activeConv.groupe?.sujet && <span style={{ color: "#0d6efd" }}>{activeConv.groupe.sujet} · </span>}
                      {activeConv.groupe?.nb_membres}/{activeConv.groupe?.max_membres} membres
                    </div>
                  </div>
                  {user?.is_staff && activeConv.groupe?.teacher_id === user?.id && (
                    <button
                      className="ql-msg-action-btn"
                      title="Gérer les membres"
                      onClick={() => setShowGroupMembers(v => !v)}
                      style={showGroupMembers ? { background: "#eff6ff", color: "#0d6efd" } : {}}
                    >
                      <i className="bi bi-person-lines-fill"></i>
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <Avatar src={activeConv.other_participant?.photo_url} name={activeConv.other_participant?.nom_complet} size="sm" />
                    {activeConv.other_participant?.is_online && <span className="ql-msg-online"></span>}
                  </div>
                  <div className="ms-2 flex-grow-1">
                    <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>{activeConv.other_participant?.nom_complet}</div>
                    <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
                      {activeConv.other_participant?.matieres?.length > 0
                        ? <span style={{ color: "#f59e0b", fontWeight: 500 }}>
                            <i className="bi bi-journal-bookmark me-1"></i>
                            {activeConv.other_participant.matieres.map(m => m.name).join(" · ")}
                          </span>
                        : activeConv.other_participant?.classe
                          ? <span style={{ color: "#6366f1", fontWeight: 500 }}>
                              <i className="bi bi-mortarboard me-1"></i>
                              {activeConv.other_participant.classe}
                            </span>
                          : activeConv.other_participant?.is_online
                            ? <><span style={{ color: "#22c55e" }}>●</span> En ligne</>
                            : "Hors ligne"
                      }
                    </div>
                  </div>
                  {user?.is_staff && !user?.is_superuser
                    && activeConv.other_participant?.role === "Élève"
                    && !activeConv.other_participant?.is_comptable && (
                    <button
                      className="ql-msg-action-btn ql-msg-call-btn"
                      title="Appeler en visio"
                      onClick={() => setCallModal({
                        targetUserId: activeConv.other_participant.id,
                        targetName: activeConv.other_participant.nom_complet,
                      })}
                    >
                      <i className="bi bi-camera-video-fill"></i>
                    </button>
                  )}
                  {(user?.is_staff || user?.is_superuser)
                    && activeConv.other_participant?.role === "Élève"
                    && !activeConv.other_participant?.is_comptable && (
                    <button
                      className="ql-msg-action-btn"
                      title="Confier à un autre professeur"
                      onClick={() => {
                        setShowReassign(true);
                        setReassignLoading(true);
                        api.get(`messages/${activeConv.id}/reassigner/`)
                          .then(r => setReassignTeachers(r.data || []))
                          .catch(() => setReassignTeachers([]))
                          .finally(() => setReassignLoading(false));
                      }}
                    >
                      <i className="bi bi-arrow-left-right"></i>
                    </button>
                  )}
                </>
              )}
              {compact && (
                <button className="ql-msg-back-btn" onClick={closeConv} title="Fermer">
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>

            {ticketActif && !activeConv.is_groupe && (perimetre === "assistance" || isStudent) && (
              <div className="ql-ticket-detail">
                <div className="ql-ticket-detail-infos">
                  <span className={`ql-ticket-chip ql-ticket-chip--${TON_TICKET[ticketActif.statut]}`}>{ticketActif.statut_label}</span>
                  {ticketActif.matiere && <span><i className="bi bi-journal-bookmark me-1"></i>{ticketActif.matiere.name}</span>}
                  {ticketActif.sujet && <span className="ql-ticket-sujet" title={ticketActif.sujet}><i className="bi bi-pin-angle me-1"></i>{ticketActif.sujet}</span>}
                  {ticketActif.statut === "en_attente" && <>
                    <span><i className="bi bi-people me-1"></i>Position : {ticketActif.estimation?.position}</span>
                    <span><i className="bi bi-stopwatch me-1"></i>{texteEstimation(ticketActif.estimation)}</span>
                  </>}
                  <span className="ql-ticket-maj">Reçue à {heure(ticketActif.arrivee)}{ticketActif.mise_a_jour ? ` · mise à jour ${heure(ticketActif.mise_a_jour)}` : ""}</span>
                </div>
                {!isStudent && ticketActif.enseignant?.id === user?.id && ticketActif.ouvert && (
                  <div className="ql-ticket-actions">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => {
                      setShowReassign(true);
                      setReassignLoading(true);
                      api.get(`messages/${activeConv.id}/reassigner/`)
                        .then(r => setReassignTeachers(r.data || []))
                        .catch(() => setReassignTeachers([]))
                        .finally(() => setReassignLoading(false));
                    }}>
                      <i className="bi bi-arrow-left-right me-1"></i>Confier à un autre professeur
                    </button>
                    {ticketActif.statut === "en_attente" && (
                      <button className="btn btn-sm btn-primary" onClick={() => actionTicket(ticketActif, "prendre")}>
                        <i className="bi bi-play-fill me-1"></i>Prendre en charge
                      </button>
                    )}
                    <button className="btn btn-sm btn-outline-success" onClick={() => actionTicket(ticketActif, "terminer")}>
                      <i className="bi bi-check2 me-1"></i>Terminer
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Panneau membres (professeur) ── */}
            {showGroupMembers && activeConv.is_groupe && (
              <div className="ql-groupe-members-panel">
                <div className="ql-groupe-members-title">
                  <i className="bi bi-people me-2"></i>Membres du groupe
                  <span className="ms-2" style={{ fontSize: "0.78rem", color: "#94a3b8" }}>
                    ({activeConv.groupe?.nb_membres}/{activeConv.groupe?.max_membres})
                  </span>
                </div>
                {(activeConv.groupe?.membres || []).map(m => (
                  <div key={m.user_id} className="ql-groupe-member-row">
                    <Avatar src={m.photo_url} name={m.nom} size="xs" />
                    <div className="ms-2 flex-grow-1">
                      <div style={{ fontSize: "0.83rem", fontWeight: 600 }}>{m.nom}</div>
                      <span className={`ql-membre-statut ql-membre-statut--${m.statut}`}>{m.statut}</span>
                    </div>
                    <div className="ql-groupe-member-actions">
                      {m.statut === "actif" && (
                        <button className="btn btn-sm btn-outline-warning py-0 px-1" title="Bloquer"
                          onClick={() => doMemberAction(activeConv.groupe.id, m.user_id, "bloquer")}>
                          <i className="bi bi-slash-circle"></i>
                        </button>
                      )}
                      {m.statut === "bloque" && (
                        <button className="btn btn-sm btn-outline-success py-0 px-1" title="Débloquer"
                          onClick={() => doMemberAction(activeConv.groupe.id, m.user_id, "debloquer")}>
                          <i className="bi bi-check-circle"></i>
                        </button>
                      )}
                      {m.statut !== "exclu" && (
                        <button className="btn btn-sm btn-outline-danger py-0 px-1 ms-1" title="Exclure"
                          onClick={() => doMemberAction(activeConv.groupe.id, m.user_id, "exclure")}>
                          <i className="bi bi-person-x"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Drawing canvas (overlay) ── */}
            {showDrawing && (
              <DrawingCanvas
                onSend={handleSendDrawing}
                onClose={() => setShowDrawing(false)}
              />
            )}

            {/* ── Appel vidéo sortant (overlay) ── */}
            {callModal && (
              <VideoCallModal
                mode="outgoing"
                targetUserId={callModal.targetUserId}
                targetName={callModal.targetName}
                onClose={() => {
                  setCallModal(null);
                  if (activeConvRef.current) {
                    api.get(`messages/${activeConvRef.current.id}/`).then(r => setMessages(r.data.messages)).catch(() => {});
                  }
                }}
              />
            )}

            {/* Thread */}
            <div ref={messagesThreadRef} className="ql-msg-thread" style={showDrawing ? { display: "none" } : {}}>
              {loadingMsgs && (
                <div className="text-center py-4"><div className="spinner-border text-primary spinner-border-sm"></div></div>
              )}
              {messages.map((msg, i) => {
                const isMe = msg.sender_id === user?.id;
                const showDate = i === 0 || new Date(msg.created_at).toDateString() !== new Date(messages[i - 1].created_at).toDateString();
                const showSender = activeConv.is_groupe && !isMe && (i === 0 || messages[i - 1].sender_id !== msg.sender_id);
                if (msg.msg_type === "appel") {
                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && <div className="ql-msg-date-sep">{formatDate(msg.created_at)}</div>}
                      <div className="ql-msg-call-bubble">
                        <i className="bi bi-camera-video"></i>{msg.content}
                      </div>
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={msg.id}>
                    {showDate && <div className="ql-msg-date-sep">{formatDate(msg.created_at)}</div>}
                    <div
                      className={`ql-msg-row${isMe ? " me" : " them"}`}
                      onMouseEnter={() => setHoveredMsgId(msg.id)}
                      onMouseLeave={() => setHoveredMsgId(null)}
                    >
                      {!isMe && (
                        <div className="ql-msg-bubble-av">
                          <Avatar src={activeConv.is_groupe ? msg.sender_photo : activeConv.other_participant?.photo_url}
                                  name={activeConv.is_groupe ? msg.sender_name : activeConv.other_participant?.nom_complet}
                                  size="xs" />
                        </div>
                      )}
                      {showSender && <div className="ql-msg-sender-name">{msg.sender_name}</div>}

                      <div className="ql-msg-bubble-wrap">
                        {hoveredMsgId === msg.id && !editingMsg && !msg.deleted && (
                          <div className={`ql-msg-hover-actions${isMe ? " hover-me" : " hover-them"}`}>
                            <button
                              className="ql-msg-reply-btn"
                              title="Répondre à ce message"
                              onClick={() => { setReplyTo(msg); mathEditorRef.current?.focus(); }}
                            >
                              <i className="bi bi-reply-fill"></i>
                            </button>
                            {isMe && msg.msg_type === "text" && !msg.deleted && (
                              <button
                                className="ql-msg-reply-btn"
                                title="Modifier ce message"
                                onClick={() => startEdit(msg)}
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </button>
                            )}
                            {(() => {
                              const isTeacher = user?.is_staff || user?.is_superuser;
                              const withinDelay = (Date.now() - new Date(msg.created_at).getTime()) < 5 * 60 * 1000;
                              const canDel = (isMe && (isTeacher || withinDelay)) || (!isMe && isTeacher);
                              return canDel ? (
                                <button
                                  className="ql-msg-reply-btn ql-msg-delete-btn"
                                  title="Supprimer ce message"
                                  onClick={() => deleteMsg(msg)}
                                >
                                  <i className="bi bi-trash-fill"></i>
                                </button>
                              ) : null;
                            })()}
                          </div>
                        )}
                      <div className={`ql-msg-bubble${isMe ? " bubble-me" : " bubble-them"}${msg.deleted ? " bubble-deleted" : ""}`}>
                        {/* Citation du message répondu */}
                        {msg.reply_to && !msg.deleted && (
                          <div className={`ql-msg-quote${isMe ? " quote-me" : " quote-them"}`}>
                            <div className="ql-msg-quote-author">{msg.reply_to.sender_name}</div>
                            <div className="ql-msg-quote-content">
                              {msg.reply_to.deleted
                                ? <em style={{ opacity: 0.6 }}>Message supprimé</em>
                                : msg.reply_to.msg_type === "text"
                                  ? <MathText text={msg.reply_to.content} />
                                  : msg.reply_to.msg_type === "pdf"
                                    ? `📎 ${msg.reply_to.file_name || "Document PDF"}`
                                    : msg.reply_to.msg_type === "image"
                                      ? "🖼 Image"
                                      : msg.reply_to.msg_type === "audio"
                                        ? "🎤 Message vocal"
                                        : msg.reply_to.msg_type === "video"
                                          ? "🎥 Vidéo"
                                          : msg.reply_to.msg_type === "devoir_rendu"
                                            ? `📝 Rendu de devoir`
                                            : msg.reply_to.msg_type === "devoir_correction"
                                              ? `✅ Correction de devoir`
                                              : msg.reply_to.msg_type === "eval_rendu"
                                                ? `📝 Rendu d'évaluation`
                                                : msg.reply_to.msg_type === "eval_correction"
                                                  ? `✅ Correction d'évaluation`
                                                  : msg.reply_to.content}
                            </div>
                          </div>
                        )}
                        {msg.deleted ? (
                          <div className="ql-msg-deleted-label">
                            <i className="bi bi-slash-circle me-1"></i>Message supprimé
                          </div>
                        ) : null}
                        {!msg.deleted && msg.msg_type === "text" && (
                          editingMsg?.id === msg.id ? (
                            <div className="ql-msg-edit-wrap">
                              <textarea
                                className="ql-msg-edit-input"
                                value={editContent}
                                onChange={e => setEditContent(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); } if (e.key === "Escape") cancelEdit(); }}
                                autoFocus
                                rows={Math.min(6, editContent.split("\n").length + 1)}
                              />
                              <div className="ql-msg-edit-actions">
                                <span style={{ fontSize: "0.72rem", color: "rgba(255,255,255,.7)" }}>Entrée pour sauver · Échap pour annuler</span>
                                <button className="ql-msg-edit-save" onClick={saveEdit}><i className="bi bi-check-lg"></i></button>
                                <button className="ql-msg-edit-cancel" onClick={cancelEdit}><i className="bi bi-x-lg"></i></button>
                              </div>
                            </div>
                          ) : (
                            <div className="ql-msg-text" translate="no"><MathText text={msg.content} /></div>
                          )
                        )}

                        {!msg.deleted && msg.msg_type === "pdf" && (
                          <div className="ql-msg-file-card">
                            <i className="bi bi-file-earmark-pdf-fill text-danger" style={{ fontSize: "1.6rem" }}></i>
                            <div className="flex-grow-1 overflow-hidden">
                              <div style={{ fontSize: "0.83rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{msg.file_name || "Document.pdf"}</div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>PDF{msg.file_size ? ` · ${fileSize(msg.file_size)}` : ""}</div>
                            </div>
                            <DownloadLink url={msg.file_url} filename={msg.file_name || "document.pdf"} mimeType="application/pdf" downloadOnWeb={false} titre={msg.file_name || "Document"} className="ql-msg-file-dl" title="Voir">
                              <i className="bi bi-eye"></i>
                            </DownloadLink>
                            <DownloadLink url={msg.file_url} filename={msg.file_name || "document.pdf"} mimeType="application/pdf" className="ql-msg-file-dl" title="Télécharger">
                              <i className="bi bi-download"></i>
                            </DownloadLink>
                          </div>
                        )}

                        {!msg.deleted && msg.msg_type === "image" && (
                          <div className="ql-msg-image-wrap" onClick={() => setLightboxSrc(msg.file_url)}>
                            <img src={msg.file_url} alt="" className="ql-msg-image" loading="lazy" />
                            <div className="ql-msg-image-overlay"><i className="bi bi-zoom-in"></i></div>
                          </div>
                        )}

                        {!msg.deleted && msg.msg_type === "audio" && (
                          <audio controls src={msg.file_url} className="ql-msg-audio" />
                        )}

                        {!msg.deleted && msg.msg_type === "video" && (
                          <video controls src={msg.file_url} className="ql-msg-video" />
                        )}

                        {!msg.deleted && msg.msg_type === "devoir_rendu" && (
                          <RenduCard
                            dr={msg.devoir_rendu}
                            user={user}
                            label="devoir"
                            correctEndpoint={`devoirs/rendus/${msg.devoir_rendu?.id}/correct/`}
                            onCorrect={(updated) => setMessages(prev => prev.map(m =>
                              m.id === msg.id ? { ...m, devoir_rendu: { ...m.devoir_rendu, ...updated } } : m
                            ))}
                            onCorrigeSent={(sent) => setMessages(prev => prev.map(m =>
                              m.id === msg.id ? { ...m, devoir_rendu: { ...m.devoir_rendu, corrige_envoye_at: sent.created_at } } : m
                            ))}
                            onStudioSent={ajouterMessages}
                          />
                        )}

                        {!msg.deleted && msg.msg_type === "assistance" && (
                          <AssistanceCard
                            data={msg.assistance}
                            user={user}
                            onSent={(sent) => setMessages(prev => prev.map(m =>
                              m.id === msg.id ? { ...m, assistance: { ...m.assistance, correction_envoyee_at: sent.created_at } } : m
                            ))}
                          />
                        )}

                        {!msg.deleted && msg.msg_type === "corrige" && <CorrigeCard content={msg.content} fileUrl={msg.file_url} />}

                        {!msg.deleted && msg.msg_type === "depense_validation" && (
                          <DepenseValidationCard
                            data={msg.depense}
                            onChange={(depense) => setMessages(prev => prev.map(m =>
                              m.depense?.id === depense.id ? { ...m, depense } : m
                            ))}
                          />
                        )}

                        {!msg.deleted && msg.msg_type === "contenu_validation" && (
                          <ContenuValidationCard
                            data={msg.contenu_cm}
                            onChange={(contenu) => setMessages(prev => prev.map(m =>
                              m.contenu_cm?.id === contenu.id ? { ...m, contenu_cm: contenu } : m
                            ))}
                          />
                        )}

                        {!msg.deleted && msg.msg_type === "devoir_correction" && (
                          <CorrectionCard dr={msg.devoir_rendu} label="Devoir" fileUrl={msg.file_url} user={user} kind="devoir" onAjout={ajouterMessages} />
                        )}

                        {!msg.deleted && msg.msg_type === "eval_rendu" && (
                          <RenduCard
                            dr={msg.evaluation_rendu}
                            user={user}
                            label="évaluation"
                            correctEndpoint={`evaluations/rendus/${msg.evaluation_rendu?.id}/correct/`}
                            onCorrect={(updated) => setMessages(prev => prev.map(m =>
                              m.id === msg.id ? { ...m, evaluation_rendu: { ...m.evaluation_rendu, ...updated } } : m
                            ))}
                            onCorrigeSent={(sent) => setMessages(prev => prev.map(m =>
                              m.id === msg.id ? { ...m, evaluation_rendu: { ...m.evaluation_rendu, corrige_envoye_at: sent.created_at } } : m
                            ))}
                            onStudioSent={ajouterMessages}
                          />
                        )}

                        {!msg.deleted && msg.msg_type === "eval_correction" && (
                          <CorrectionCard dr={msg.evaluation_rendu} label="Évaluation" fileUrl={msg.file_url} user={user} kind="evaluation" onAjout={ajouterMessages} />
                        )}

                        {!msg.deleted && msg.msg_type === "contestation" && (
                          <ContestationCard data={msg.contestation} user={user} onAjout={ajouterMessages}
                            onChange={(c) => setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, contestation: c } : m))} />
                        )}

                        <div className="ql-msg-meta-row">
                          {msg.edited && <span className="ql-msg-edited-tag">modifié</span>}
                          <span>{new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                          {isMe && (
                            <i
                              className={`bi ms-1 ${msg.is_read ? "bi-check2-all ql-tick-read" : "bi-check2 ql-tick-sent"}`}
                              title={msg.is_read ? "Lu" : "Envoyé"}
                            ></i>
                          )}
                        </div>
                      </div>
                      </div>{/* fin ql-msg-bubble-wrap */}
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* MathLive affiche son propre clavier virtuel quand l'éditeur est focus */}

            {/* Input bar */}
            {/* Barre de réponse */}
            {replyTo && (
              <div className="ql-msg-reply-bar">
                <i className="bi bi-reply-fill me-2" style={{ color: "#6366f1", fontSize: "1rem" }}></i>
                <div className="ql-msg-reply-bar-content">
                  <div className="ql-msg-reply-bar-author">{replyTo.sender_name}</div>
                  <div className="ql-msg-reply-bar-text">
                    {replyTo.msg_type === "text"
                      ? <MathText text={replyTo.content} />
                      : replyTo.msg_type === "pdf"
                        ? `📎 ${replyTo.file_name || "Document PDF"}`
                        : replyTo.msg_type === "image"
                          ? "🖼 Image"
                          : replyTo.msg_type === "audio"
                            ? "🎤 Message vocal"
                            : replyTo.msg_type === "video"
                              ? "🎥 Vidéo"
                              : replyTo.content}
                  </div>
                </div>
                <button className="ql-msg-reply-bar-close" onClick={() => setReplyTo(null)} title="Annuler la réponse">
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            )}
            {pendingFile && !user?.is_staff && !user?.is_superuser && !user?.is_comptable && !user?.is_community_manager && !user?.is_secretaire && !user?.is_brand_ambassador && (
              <div className="ql-conseil-scan" style={{ margin: "0 12px 6px" }}>
                <i className="bi bi-camera"></i>
                <span>Tu peux traiter en dehors de l'application : après avoir traité sur ta copie, scanne-la avec une application comme CamScanner et télécharge-la dans l'application avant de soumettre.</span>
              </div>
            )}
            {pendingFile && (
              <div className="ql-msg-pending-file-bar">
                {pendingFile.type === "image" && pendingFile.previewUrl && (
                  <img src={pendingFile.previewUrl} alt="" className="ql-msg-pending-thumb" />
                )}
                {pendingFile.type !== "image" && (
                  <i className={`bi ${pendingFile.type === "pdf" ? "bi-file-earmark-pdf-fill text-danger" : "bi-film text-primary"}`} style={{ fontSize: "1.4rem" }}></i>
                )}
                <div className="ql-msg-pending-info">
                  <div className="ql-msg-pending-name">{pendingFile.file.name}</div>
                  <div className="ql-msg-pending-size">{fileSize(pendingFile.file.size)}</div>
                </div>
                <button className="ql-msg-pending-remove" onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            )}
            {sendError && (
              <div style={{ background: "#fee2e2", color: "#b91c1c", fontSize: "0.78rem", padding: "4px 14px", borderTop: "1px solid #fca5a5" }}>
                {sendError}
              </div>
            )}
            {activeConv.is_groupe && activeConv.mon_statut === "bloque" && (
              <div className="ql-groupe-blocked-bar">
                <i className="bi bi-slash-circle me-2"></i>Vous avez été bloqué dans ce groupe. Vous ne pouvez pas envoyer de messages.
              </div>
            )}
            {activeConv.is_groupe && activeConv.mon_statut === "exclu" && (
              <div className="ql-groupe-blocked-bar">
                <i className="bi bi-person-x me-2"></i>Vous avez été exclu de ce groupe.
              </div>
            )}
            <div className="ql-msg-input-bar" style={activeConv.is_groupe && activeConv.mon_statut !== "actif" ? { display: "none" } : {}}>
              <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*,.pdf,video/*" onChange={handleFileSelect} />
              {/* Boutons secondaires : visibles sur desktop, groupés sous "+" sur mobile */}
              <div className="ql-msg-extra-btns">
                <button
                  className="ql-msg-action-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title="Joindre une image, un PDF ou une vidéo"
                ><i className="bi bi-paperclip"></i></button>
                <button
                  className={`ql-msg-action-btn${recording ? " recording" : ""}`}
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onTouchStart={startRecording}
                  onTouchEnd={stopRecording}
                  title="Maintenir pour enregistrer un message vocal"
                ><i className="bi bi-mic-fill"></i></button>
                <button
                  className={`ql-msg-action-btn${showDrawing ? " active" : ""}`}
                  onClick={() => setShowDrawing(v => !v)}
                  title="Dessiner une figure"
                ><i className="bi bi-brush"></i></button>
                <button
                  className={`ql-msg-action-btn ql-msg-math-btn${showMathKb ? " active" : ""}`}
                  onClick={() => { setShowMathKb(v => !v); setShowDrawing(false); }}
                  title="Clavier mathématique"
                ><span>Σ</span></button>
              </div>
              {showMathKb && (
                <div className="ql-msg-mathkb-wrap">
                  <MathKeyboard
                    onInsert={insertAtCursor}
                    insertLabel="Insérer dans le message"
                    onClose={() => setShowMathKb(false)}
                  />
                </div>
              )}
              <textarea
                ref={_taRef}
                className="ql-msg-textarea"
                placeholder="Écrire un message…"
                rows={1}
                onChange={e => {
                  const v = e.target.value;
                  setMsgHasContent(!!v.trim());
                  if (activeConv?.id) saveDraft(activeConv.id, v);
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
                }}
                onFocus={() => setShowDrawing(false)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                }}
              />
              <button
                className="ql-msg-send-btn"
                onClick={() => sendMessage()}
                disabled={sending || (!msgHasContent && !recording && !pendingFile)}
              >
                {sending
                  ? <span className="spinner-border spinner-border-sm"></span>
                  : <i className="bi bi-send-fill"></i>
                }
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── RIGHT: contacts + info ──────────────────── */}
      <div className="ql-msg-right-panel">
        <div className="ql-msg-right-card">
          <div className="ql-msg-right-title">Contacts fréquents</div>
          {contacts.slice(0, 4).map(c => (
            <div key={c.id} className="ql-msg-contact-row" onClick={() => createConversation(c.id)}>
              <div style={{ position: "relative" }}>
                <Avatar src={c.photo_url} name={c.nom_complet} size="sm" />
                {c.is_online && <span className="ql-msg-online"></span>}
              </div>
              <div className="ms-2 flex-grow-1 overflow-hidden">
                <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>{c.nom_complet}</div>
                {c.matieres?.length > 0
                  ? <div style={{ fontSize: "0.72rem", color: "#f59e0b", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      <i className="bi bi-journal-bookmark me-1"></i>{c.matieres.map(m => m.name).join(", ")}
                    </div>
                  : c.classe
                    ? <div style={{ fontSize: "0.72rem", color: "#6366f1", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <i className="bi bi-mortarboard me-1"></i>{c.classe}
                      </div>
                    : <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{c.role}</div>
                }
              </div>
              <i className="bi bi-three-dots text-muted" style={{ fontSize: "0.8rem" }}></i>
            </div>
          ))}
        </div>

        <div className="ql-msg-right-card">
          <div className="ql-msg-right-title">Informations utiles</div>
          {[
            { icon: "bi-clock",          title: "Heures d'assistance",    sub: "Lun - Ven : 8h00 - 22h00" },
            { icon: "bi-chat-dots",      title: "Temps de réponse moyen", sub: "Moins de 24h" },
            { icon: "bi-question-circle",title: "Conseils",                sub: "Soyez précis dans vos questions." },
          ].map((item, i) => (
            <div key={i} className="ql-msg-info-row">
              <i className={`bi ${item.icon} text-primary`} style={{ fontSize: "1.1rem", minWidth: 22 }}></i>
              <div className="ms-2">
                <div style={{ fontSize: "0.82rem", fontWeight: 500 }}>{item.title}</div>
                <div style={{ fontSize: "0.76rem", color: "#64748b" }}>{item.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Lightbox image ──────────────────────────── */}
      {lightboxSrc && (
        <div className="ql-msg-overlay" onClick={() => setLightboxSrc(null)}>
          <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }} onClick={e => e.stopPropagation()}>
            <img src={lightboxSrc} alt="" style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 10, display: "block" }} />
            <DownloadLink url={lightboxSrc} filename={lightboxSrc.split("?")[0].split("/").pop()} mimeType="image/jpeg"
              style={{ position: "absolute", top: 8, right: 44, background: "rgba(0,0,0,.55)", color: "#fff", borderRadius: 6, padding: "4px 8px", fontSize: "0.85rem", textDecoration: "none" }}>
              <i className="bi bi-download"></i>
            </DownloadLink>
            <button onClick={() => setLightboxSrc(null)}
              style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,.55)", color: "#fff", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      )}

      {/* ── Modal new conversation ──────────────────── */}
      {showNewConv && (
        <div className="ql-msg-overlay" onClick={() => setShowNewConv(false)}>
          <div className="ql-msg-modal" onClick={e => e.stopPropagation()}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-bold">Nouveau message</span>
              <button className="btn-close btn-sm" onClick={() => setShowNewConv(false)}></button>
            </div>
            <p className="text-muted mb-3" style={{ fontSize: "0.87rem" }}>Choisissez un contact</p>
            {contactsVisibles.length === 0 && <p className="text-muted" style={{ fontSize: "0.85rem" }}>Aucun contact.</p>}
            {contactsVisibles.map(c => (
              <div key={c.id} className="ql-msg-contact-row" onClick={() => createConversation(c.id)}>
                <div style={{ position: "relative" }}>
                  <Avatar src={c.photo_url} name={c.nom_complet} size="sm" />
                  {c.is_online && <span className="ql-msg-online"></span>}
                </div>
                <div className="ms-2">
                  <div style={{ fontSize: "0.88rem", fontWeight: 600 }}>{c.nom_complet}</div>
                  <div style={{ fontSize: "0.77rem", color: "#64748b" }}>{c.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Modal réassignation ─────────────────────── */}
      {showReassign && (
        <div className="ql-msg-overlay" onClick={() => setShowReassign(false)}>
          <div className="ql-msg-modal" onClick={e => e.stopPropagation()}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-bold">Confier l'élève à un autre professeur</span>
              <button className="btn-close btn-sm" onClick={() => setShowReassign(false)}></button>
            </div>
            <p className="text-muted mb-3" style={{ fontSize: "0.87rem" }}>
              La conversation avec {activeConv?.other_participant?.nom_complet} sera transférée à l'enseignant choisi.
            </p>
            {reassignLoading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"></div></div>
            ) : reassignTeachers.length === 0 ? (
              <p className="text-muted" style={{ fontSize: "0.85rem" }}>Aucun autre enseignant disponible.</p>
            ) : (
              reassignTeachers.map(t => (
                <div key={t.id} className="ql-msg-contact-row" onClick={() => doReassign(t.id)}>
                  <Avatar name={t.nom} size="sm" />
                  <div className="ms-2">
                    <div style={{ fontSize: "0.88rem", fontWeight: 600 }}>{t.nom}</div>
                    {(t.disponibilite || t.demandes_ouvertes != null) && (
                      <div style={{ fontSize: "0.75rem", color: "#1558c9" }}>
                        {t.disponibilite}{t.demandes_ouvertes != null ? ` · ${t.demandes_ouvertes} demande(s) en cours` : ""}
                      </div>
                    )}
                    {t.matieres?.length > 0 && (
                      <div style={{ fontSize: "0.77rem", color: "#64748b" }}>{t.matieres.map(m => m.name).join(" · ")}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
